import { clsx } from 'clsx'
import { DoorOpen } from 'lucide-react'

import { blurAfterMouseClick } from './blurAfterMouseClick'

import styles from './ActionBar.module.scss'

import type { GtSpeaker, GtTurn, MeetingAttributionGtSnapshotTurn } from '@/api/internal'
import { toSpeakerIdentity } from '@/pages/app/AttributionViewer/annotate/gtDerivations'
import { GT_LABELS, type GtLabel, labelOfSpeaker } from '@/pages/app/AttributionViewer/annotate/gtModel'
import type { useGtAudio } from '@/pages/app/AttributionViewer/annotate/useGtAudio'
import { type SpeakerStyleMap, styleForSpeaker } from '@/pages/app/AttributionViewer/components/colors'
import { formatMs } from '@/pages/app/AttributionViewer/components/Swimlane'

export interface ActionBarProps {
  activeTurn: GtTurn | null
  snapshot: MeetingAttributionGtSnapshotTurn | null
  speaker: GtSpeaker | null
  selectedCount: number
  hotkeySpeakers: Map<string, GtSpeaker>
  /** Speaker id -> the name it is shown and scored under (room voices read "<room> · voice N"). */
  displayNames: ReadonlyMap<string, string>
  speakerStyles: SpeakerStyleMap
  position: { index: number; total: number }
  canUndo: boolean
  readOnly: boolean
  audio: ReturnType<typeof useGtAudio>
  onAssign: (speakerId: string) => void
  onLabel: (label: GtLabel) => void
  /** Edit a single voice or group the selected speakers. */
  onRoom: () => void
  onConfirm: () => void
  onDelete: () => void
  onClear: () => void
  onEditText: () => void
  onSplit: () => void
  /** Set while a text editor is open: the key that splits at the caret. */
  splitHotkey: string | null
  onNote: () => void
  onUndo: () => void
  onPlayToggle: () => void
  onRestart: () => void
  onSeek: (ms: number) => void
  onShortcuts: () => void
}

interface ActionButtonProps {
  label: React.ReactNode
  hotkey?: string
  variant?: 'default' | 'destructive' | 'accept'
  disabled?: boolean
  /** Leave focus (and the caret) in the text editor when this button is clicked. */
  keepFocus?: boolean
  selectionTarget?: boolean
  onClick: () => void
}

const keepEditorFocus = (event: React.MouseEvent<HTMLButtonElement>) => event.preventDefault()

const ActionButton = ({
  label,
  hotkey,
  variant = 'default',
  disabled,
  keepFocus,
  selectionTarget,
  onClick,
}: ActionButtonProps) => (
  <button
    type="button"
    data-speaker-selection-target={selectionTarget || undefined}
    className={clsx(
      styles.action,
      variant === 'destructive' && styles.destructive,
      variant === 'accept' && styles.accept
    )}
    disabled={disabled}
    onMouseDown={keepFocus ? keepEditorFocus : undefined}
    onClick={onClick}
  >
    {label}
    {hotkey && <span className={styles.kb}>{hotkey}</span>}
  </button>
)

export const ActionBar = ({
  activeTurn,
  snapshot,
  speaker,
  selectedCount,
  hotkeySpeakers,
  displayNames,
  speakerStyles,
  position,
  canUndo,
  readOnly,
  audio,
  onAssign,
  onLabel,
  onRoom,
  onConfirm,
  onDelete,
  onClear,
  onEditText,
  onSplit,
  splitHotkey,
  onNote,
  onUndo,
  onPlayToggle,
  onRestart,
  onSeek,
  onShortcuts,
}: ActionBarProps) => {
  const editingDisabled = readOnly || !activeTurn
  const audioReady = audio.state === 'present'
  const otherSpeakers = Array.from(hotkeySpeakers.entries()).filter(([, sp]) => sp.id !== speaker?.id)
  const hint = snapshot?.hint
  const durationMs = audio.durationMs ?? 0
  const nameOf = (sp: GtSpeaker): string => displayNames.get(sp.id) ?? sp.name
  const speakerName = speaker ? nameOf(speaker) : null

  return (
    <div className={styles.bar} role="region" aria-label="Turn actions" onClick={blurAfterMouseClick}>
      <div className={styles.ctx}>
        {activeTurn ? (
          <>
            <span className={styles.pos}>
              {position.index + 1}/{position.total}
            </span>
            <span className={styles.pos}>
              {formatMs(activeTurn.start_ms)}–{formatMs(activeTurn.end_ms)}
            </span>
            <span className={styles.draft}>
              speaker: <strong>{speakerName ?? 'Unassigned'}</strong>
            </span>
            {activeTurn.state === 'confirmed' && (
              <span className={clsx(styles.pill, styles.confirmed)}>✓ Confirmed</span>
            )}
            {activeTurn.state === 'deleted' && <span className={clsx(styles.pill, styles.deleted)}>Deleted</span>}
            {snapshot?.production && snapshot.production.label !== speakerName && (
              <span className={clsx(styles.pill, styles.prod)}>prod: {snapshot.production.label}</span>
            )}
            {hint && hint.name !== speakerName && (
              <span className={styles.why}>
                hint: {hint.name} ({hint.why.join(' · ')})
              </span>
            )}
          </>
        ) : (
          <span className={styles.draft}>No turn selected</span>
        )}
        <span className={styles.spacer} />
        <button type="button" className={styles.linkButton} onClick={onShortcuts}>
          <span className={styles.kb}>?</span> Shortcuts
        </button>
      </div>
      <div className={styles.acts}>
        <ActionButton
          label="✓ Accept"
          hotkey="⏎"
          variant="accept"
          disabled={editingDisabled || !activeTurn?.speaker_id || activeTurn?.state === 'confirmed'}
          onClick={onConfirm}
        />
        {otherSpeakers.map(([hotkey, sp]) => {
          const style = styleForSpeaker(toSpeakerIdentity(sp), speakerStyles)
          return (
            <ActionButton
              key={sp.id}
              hotkey={hotkey}
              disabled={editingDisabled}
              onClick={() => onAssign(sp.id)}
              label={
                <>
                  <span className={styles.dot} style={{ backgroundColor: style.color }} />
                  {sp.kind === 'room' && <DoorOpen size={11} aria-hidden="true" />}
                  <span className={styles.speakerName}>{nameOf(sp)}</span>
                </>
              }
            />
          )
        })}
        {(Object.keys(GT_LABELS) as GtLabel[]).map((label) => (
          <ActionButton
            key={label}
            label={GT_LABELS[label].name}
            hotkey={GT_LABELS[label].hotkey}
            disabled={editingDisabled || (speaker != null && labelOfSpeaker(speaker) === label)}
            onClick={() => onLabel(label)}
          />
        ))}
        <ActionButton
          label={
            <>
              <DoorOpen size={11} aria-hidden="true" />
              {selectedCount > 1
                ? `Group ${selectedCount} speakers`
                : speaker?.kind === 'room'
                  ? 'Name / remove voice'
                  : 'Rename / join room'}
            </>
          }
          hotkey="G"
          selectionTarget
          disabled={readOnly || (selectedCount === 0 && (!speaker || labelOfSpeaker(speaker) !== null))}
          onClick={onRoom}
        />
        <ActionButton label="✕ Delete" hotkey="D" variant="destructive" disabled={editingDisabled} onClick={onDelete} />
        <span className={styles.divider} />
        <ActionButton label="✎ Edit text" hotkey="E" disabled={editingDisabled} onClick={onEditText} />
        <ActionButton
          label={splitHotkey ? '✂ Split at cursor' : '✂ Split'}
          hotkey={splitHotkey ?? 'S'}
          disabled={editingDisabled}
          keepFocus
          onClick={onSplit}
        />
        <ActionButton label="Note" hotkey="N" disabled={editingDisabled} onClick={onNote} />
        {activeTurn?.state && <ActionButton label="↺ Clear" hotkey="⌫" disabled={editingDisabled} onClick={onClear} />}
        {canUndo && <ActionButton label="Undo last" hotkey="Z" disabled={readOnly} onClick={onUndo} />}
      </div>
      <div className={styles.audiobar}>
        <button
          type="button"
          className={styles.playButton}
          disabled={!audioReady || !activeTurn}
          onClick={onPlayToggle}
          aria-label={audio.isPlaying ? 'Pause' : 'Play'}
        >
          {audio.isPlaying ? '❚❚ Pause' : '▶ Play'}
        </button>
        <button
          type="button"
          className={styles.restartButton}
          disabled={!audioReady || !activeTurn}
          onClick={onRestart}
          aria-label="Restart clip"
        >
          ↺
        </button>
        <input
          type="range"
          className={styles.scrub}
          min={0}
          max={durationMs}
          step={100}
          value={Math.min(audio.audioTime ?? 0, durationMs)}
          disabled={!audioReady}
          onChange={(event) => onSeek(Number(event.target.value))}
          aria-label="Scrub audio"
        />
        <span className={styles.clock}>
          {formatMs(audio.audioTime ?? 0)} / {audio.durationMs != null ? formatMs(audio.durationMs) : '–:––'}
        </span>
        <span className={styles.hint}>
          Space play turn · hold ←/→ scrub the turn at 3× · ⏎ or ↓ accept and go to next · ↑ previous · 1–9 then Q W T
          Y… assign speaker · U unknown · X echo · G room · D delete · A add speaker · E or double-click edit text · S
          split turn · ⌫ clear state (deletes ticked speakers) · Z undo · N note · ⌘S save
        </span>
      </div>
    </div>
  )
}
